import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { api, formatApiErrorDetail, API } from "../lib/api";
import { GLOBAL_REGIONS, isAtlanticRegion } from "../lib/regions";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useCurrency } from "../contexts/CurrencyContext";
import StepBrief from "./campaign_builder/StepBrief";
import StepQuestions from "./campaign_builder/StepQuestions";
import StepAudience from "./campaign_builder/StepAudience";
import StepReview from "./campaign_builder/StepReview";

const STEPS = ["Brief", "Questions", "Audience & Stimulus", "Review & Pay"];

// Pricing is fetched LIVE from POST /api/campaigns/preview-pricing — every
// number on the Invoice Summary card comes from the backend. The backend
// applies: final = CR_raw_cost + (target × tier_markup), where tier_markup
// is text=$1.50 | audio=$3.00 | video=$5.00. The wizard never hard-codes
// these numbers so future CR API updates flow through automatically.

const CANADIAN_REGIONS = GLOBAL_REGIONS;

export default function CampaignBuilder() {
  const nav = useNavigate();
  const { currency: displayCcy } = useCurrency();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    title: "",
    target_completes: 100,
    modality: "audio",            // text | audio | video
    language: "en",
    audio_retention_days: 90,
    stimulus_type: "none",
    stimulus_url: null,
    audience_sourcing_mix: { internal_panel: 0, programmatic_cloudresearch: 100, client_intercept: 0 },
    qualitative_questions: ["", "", ""],
    multiple_choice_questions: [{ question_text: "", options: ["", ""] }],
    screener_questions: [],
    video_enabled: false,          // legacy mirror of modality === "video"
    compare_group_id: null,
    recurrence_cadence: "none",
    compliance_mode: "standard",
    target_demographics: { region: "", gender: "" },
    // PS-mandatory study parameters. Defaults chosen to match the most
    // common Adrigo study (5-min audio-first with 50% IR fielded over a
    // week) so the wizard never launches without them but the client can
    // (and should) tune each on Step Brief.
    expected_ir: 50,
    expected_loi: 5,
    field_time: 7,
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Region typeahead
  const [regionOpen, setRegionOpen] = useState(false);
  const [regionActiveIdx, setRegionActiveIdx] = useState(-1);
  const regionWrapRef = useRef(null);

  // Feasibility (audience-reach counter)
  const [feasibility, setFeasibility] = useState(null);
  const [feasibilityLoading, setFeasibilityLoading] = useState(false);

  // Live pricing (Invoice Summary)
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  const target = Math.max(0, parseInt(form.target_completes) || 0);
  const mixTotal =
    form.audience_sourcing_mix.internal_panel +
    form.audience_sourcing_mix.programmatic_cloudresearch +
    form.audience_sourcing_mix.client_intercept;

  // ============ MC + Screener mutators ============
  const updateMc = (idx, patch) => {
    const next = [...form.multiple_choice_questions];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, multiple_choice_questions: next });
  };
  const addMc = () => {
    if (form.multiple_choice_questions.length >= 5) return;
    const _uid = `mc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setForm({ ...form, multiple_choice_questions: [...form.multiple_choice_questions, { _uid, question_text: "", options: ["", ""] }] });
  };
  const removeMc = (idx) => {
    setForm({ ...form, multiple_choice_questions: form.multiple_choice_questions.filter((_, i) => i !== idx) });
  };
  const addScreener = () => {
    if (form.screener_questions.length >= 3) return;
    const _uid = `sc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setForm({ ...form, screener_questions: [...form.screener_questions, { _uid, question_text: "", options: [{ text: "", disqualify: false }, { text: "", disqualify: true }] }] });
  };
  const updateScreener = (idx, patch) => {
    const next = [...form.screener_questions];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, screener_questions: next });
  };
  const removeScreener = (idx) => {
    setForm({ ...form, screener_questions: form.screener_questions.filter((_, i) => i !== idx) });
  };

  // ============ Region autocomplete ============
  const regionQuery = form.target_demographics.region || "";
  const regionSuggestions = useMemo(() => {
    const q = regionQuery.trim().toLowerCase();
    if (!q) return CANADIAN_REGIONS.slice(0, 8);
    return CANADIAN_REGIONS.filter((r) => r.toLowerCase().includes(q)).slice(0, 8);
  }, [regionQuery]);
  const pickRegion = (val) => {
    setForm((f) => ({ ...f, target_demographics: { ...f.target_demographics, region: val } }));
    setRegionOpen(false);
    setRegionActiveIdx(-1);
  };
  useEffect(() => {
    const onDocClick = (e) => {
      if (regionWrapRef.current && !regionWrapRef.current.contains(e.target)) setRegionOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // ============ Audience feasibility (debounced) ============
  const internalPct = form.audience_sourcing_mix.internal_panel;
  const cloudresearchPct = form.audience_sourcing_mix.programmatic_cloudresearch;
  const interceptPct = form.audience_sourcing_mix.client_intercept;
  // Stable signature of CR quotas so the feasibility counter also shrinks
  // when the user toggles a targeting chip on Step 2.
  const feasibilityCrQuotasSig = JSON.stringify(
    (form.target_demographics?.cr_quotas || []).map((q) => ({
      d: q.demographicId,
      o: [...(q.demographicOptionIds || [])].sort(),
    })),
  );
  // Country / sub-region from the new Geography Pillar. These are first-class
  // inputs to feasibility now — changing the country must move the ring.
  const pillarCountry = form.target_demographics?.country || "";
  const pillarSubregion = form.target_demographics?.subregion || "";
  // PS-mandatory study parameters flow into both feasibility AND pricing so
  // the ring + invoice reflect the actual design as the client tunes them.
  const expectedIr = form.expected_ir;
  const expectedLoi = form.expected_loi;
  const fieldTime = form.field_time;

  // When the user has NOT picked any gender chip, PS treats "no criteria"
  // as "return the raw panel size" — which includes panelists whose gender
  // is unknown, so the counter blows up to 900K while the "all 3 selected"
  // sum is ~11K. Force the counter to always reflect Male+Female+Non-binary
  // by injecting a synthetic all-genders quota into the feasibility payload
  // ONLY when the pillar has zero selections. Persisted `cr_quotas` stay
  // untouched so the actual survey qualification remains "no gender filter".
  const feasibilityQuotas = useMemo(() => {
    const baseQuotas = form.target_demographics?.cr_quotas || [];
    const selected = form.target_demographics?.pillar_gender || [];
    const demoId = form.target_demographics?.pillar_gender_demo_id;
    const allIds = form.target_demographics?.pillar_gender_all_ids || [];
    if (selected.length > 0 || !demoId || allIds.length === 0) return baseQuotas;
    // No user pick → append the union-of-all-genders synthetic quota.
    const withoutGender = baseQuotas.filter((q) => String(q.demographicId) !== String(demoId));
    return [...withoutGender, { demographicId: String(demoId), demographicOptionIds: [...allIds] }];
  }, [
    feasibilityCrQuotasSig,
    form.target_demographics?.pillar_gender,
    form.target_demographics?.pillar_gender_demo_id,
    form.target_demographics?.pillar_gender_all_ids,
    // feasibilityCrQuotasSig already tracks cr_quotas
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  useEffect(() => {
    const ctl = new AbortController();
    let cancelled = false;
    const t = setTimeout(async () => {
      if (!cancelled) setFeasibilityLoading(true);
      try {
        const { data } = await api.post("/campaigns/feasibility", {
          region: regionQuery,
          internal: internalPct,
          cloudresearch: cloudresearchPct,
          intercept: interceptPct,
          target: target || 100,
          cr_quotas: feasibilityQuotas,
          country: pillarCountry,
          subregion: pillarSubregion,
          expected_ir: expectedIr,
          expected_loi: expectedLoi,
          field_time: fieldTime,
        }, { signal: ctl.signal });
        if (!cancelled) setFeasibility(data);
      } catch (e) {
        if (!cancelled && e.name !== "CanceledError" && e.name !== "AbortError") setFeasibility(null);
      } finally {
        if (!cancelled) setFeasibilityLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); ctl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionQuery, internalPct, cloudresearchPct, interceptPct, target, feasibilityQuotas, pillarCountry, pillarSubregion, expectedIr, expectedLoi, fieldTime]);

  // ============ LIVE pricing (debounced) — drives Invoice Summary ============
  const modality = form.modality;
  const qualSig = form.qualitative_questions.filter((q) => (q || "").trim()).length;
  // Stable signature of CR quotas so the debounced effect re-fires whenever
  // the user toggles a chip in the dynamic targeting panel. We serialise to
  // a string rather than depending on the array reference so the deps array
  // doesn't churn on every render.
  const crQuotasSig = JSON.stringify(
    (form.target_demographics?.cr_quotas || []).map((q) => ({
      d: q.demographicId,
      o: [...(q.demographicOptionIds || [])].sort(),
    })),
  );
  // Country + sub-region are FIRST-CLASS pricing inputs — PS quotes a
  // different CPI for CA-panel-targeted vs global-panel-targeted studies
  // (verified: audio/IR=80% is $624 unfiltered vs $647 with country=CA).
  // We include them explicitly so the invoice re-fetches the instant the
  // Geography Pillar changes. This closes the "Review says X, Stripe
  // charges Y" gap from Feb 2026.
  const pricingCountry = form.target_demographics?.country || "";
  const pricingSubregion = form.target_demographics?.subregion || "";
  useEffect(() => {
    const ctl = new AbortController();
    let cancelled = false;
    const t = setTimeout(async () => {
      if (!cancelled) setPricingLoading(true);
      try {
        const { data } = await api.post(
          "/campaigns/preview-pricing",
          {
            modality,
            target_completes: target || 10,
            qualitative_questions: form.qualitative_questions,
            target_demographics: form.target_demographics,
            // PS-mandatory study parameters — MUST be forwarded or the
            // server defaults them (IR=50/LOI=5/field=7) and Step Review
            // renders a stale price that mismatches what /campaigns POST
            // persists at submit-time. That mismatch bit us in 2026-02
            // when Stripe charged a different amount than Review showed.
            expected_ir: form.expected_ir,
            expected_loi: form.expected_loi,
            field_time: form.field_time,
            currency: displayCcy,
          },
          { signal: ctl.signal },
        );
        if (!cancelled) setPricing(data);
      } catch (e) {
        if (!cancelled && e.name !== "CanceledError" && e.name !== "AbortError") setPricing(null);
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); ctl.abort(); };
    // form.qualitative_questions / form.target_demographics deliberately referenced via signatures
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modality, target, qualSig, regionQuery, crQuotasSig, displayCcy, form.expected_ir, form.expected_loi, form.field_time, pricingCountry, pricingSubregion]);

  const regionConflict = !isAtlanticRegion(regionQuery) && internalPct > 0;
  const fmtCount = (n) => (typeof n === "number" ? n.toLocaleString("en-CA") : "—");

  // ============ Stimulus upload ============
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/stimulus", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const type = file.type.startsWith("video") ? "video" : "image";
      setForm({ ...form, stimulus_type: type, stimulus_url: `${API.replace("/api", "")}${data.url}` });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  // ============ Submit ============
  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      if (mixTotal !== 100) throw new Error("Audience sourcing mix must total 100%.");
      if (form.qualitative_questions.filter((q) => q.trim()).length !== 3) throw new Error("Provide all 3 qualitative questions.");

      const payload = {
        title: form.title,
        target_completes: target,
        modality: form.modality,
        language: form.language,
        audio_retention_days: form.audio_retention_days,
        stimulus_type: form.stimulus_type,
        stimulus_url: form.stimulus_url,
        audience_sourcing_mix: form.audience_sourcing_mix,
        qualitative_questions: form.qualitative_questions,
        compare_group_id: form.compare_group_id,
        recurrence_cadence: form.recurrence_cadence,
        compliance_mode: form.compliance_mode,
        target_demographics: form.target_demographics,
        expected_ir: form.expected_ir,
        expected_loi: form.expected_loi,
        field_time: form.field_time,
        multiple_choice_questions: form.multiple_choice_questions
          .filter((q) => q.question_text.trim())
          .map((q) => ({ question_text: q.question_text, options: q.options.filter((o) => o.trim()) })),
        screener_questions: form.screener_questions
          .filter((q) => q.question_text.trim() && q.options.some((o) => o.text.trim()))
          .map((q) => ({
            question_text: q.question_text,
            options: q.options.filter((o) => o.text.trim()).map((o) => ({ text: o.text, disqualify: !!o.disqualify })),
          })),
      };
      const { data: created } = await api.post("/campaigns", payload);
      const origin = window.location.origin;
      // Snapshot the USD total the user JUST saw on Review so the backend
      // can refuse to bill a materially different amount. Belt-and-braces
      // against pricing-drift regressions like the Feb 2026 country-not-
      // in-deps bug where Review showed $624 USD but the persisted row
      // was actually $647 USD.
      const previewTotalUsd = pricing?.total_budget ?? null;
      const { data: checkout } = await api.post("/payments/campaign-checkout", {
        campaign_id: created.id,
        origin_url: origin,
        currency: displayCcy,
        preview_total_usd: previewTotalUsd,
      });
      window.location.href = checkout.url;
    } catch (err) {
      // If the server flagged pricing drift (409), surface the concrete
      // gap so the user knows exactly what changed. Otherwise fall
      // through to the standard formatter.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail && typeof detail === "object" && detail.code === "price_drift") {
        setError(detail.message || "The price has changed. Please go back to Review and confirm.");
        // Send the user back to Step Review so the freshly-refetched
        // pricing state renders before their next click.
        setStep(3);
      } else {
        setError(
          formatApiErrorDetail(detail)
          || err.message
          || "Something went wrong. Please try again.",
        );
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="campaign-builder">
      <Navigation />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <button onClick={() => nav("/client")} className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--rl-text-secondary)] hover:text-[var(--rl-text)] transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>
        <div className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-bold">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={i === step ? "text-[var(--rl-brand)]" : i < step ? "text-[var(--rl-text)]" : "text-[var(--rl-text-secondary)]"} data-testid={`step-label-${i}`}>{i + 1}. {s}</span>
              {i < STEPS.length - 1 && <span className="text-[var(--rl-border)]">→</span>}
            </div>
          ))}
        </div>
        <h1 className="font-display font-black text-4xl tracking-tight mt-4">{STEPS[step]}</h1>

        <div className="mt-10 bg-white border border-[var(--rl-border)] p-8 lg:p-10">
          {step === 0 && (
            <StepBrief form={form} setForm={setForm} pricing={pricing} pricingLoading={pricingLoading} />
          )}
          {step === 1 && (
            <StepQuestions
              form={form} setForm={setForm}
              addScreener={addScreener} updateScreener={updateScreener} removeScreener={removeScreener}
              addMc={addMc} updateMc={updateMc} removeMc={removeMc}
            />
          )}
          {step === 2 && (
            <StepAudience
              form={form} setForm={setForm}
              mixTotal={mixTotal}
              feasibility={feasibility} feasibilityLoading={feasibilityLoading} fmtCount={fmtCount}
              uploading={uploading} handleUpload={handleUpload}
            />
          )}
          {step === 3 && (
            <StepReview form={form} pricing={pricing} />
          )}

          {error && <div className="mt-6 text-sm text-[var(--rl-accent)] font-medium" data-testid="builder-error">{error}</div>}

          <div className="mt-10 flex items-center justify-between">
            {step > 0 ? (
              <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--rl-text-secondary)] hover:text-[var(--rl-text)] transition-colors" data-testid="builder-prev-btn">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : <span />}
            {step < STEPS.length - 1 ? (
              (() => {
                // Step Brief (step 0) requires all PS-mandatory study
                // parameters + a non-empty title before we let the wizard
                // advance. Every other step is currently free-form so
                // Next stays enabled.
                const briefIncomplete = step === 0 && (
                  !(form.title || "").trim()
                  || !(form.expected_ir >= 1 && form.expected_ir <= 100)
                  || !(form.expected_loi >= 1 && form.expected_loi <= 60)
                  || !(form.field_time >= 1 && form.field_time <= 90)
                );
                return (
                  <button
                    onClick={() => setStep(step + 1)}
                    disabled={briefIncomplete}
                    title={briefIncomplete ? "Please provide a title and valid study parameters" : undefined}
                    className="inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="builder-next-btn"
                  >
                    Next <ArrowRight className="w-4 h-4" />
                  </button>
                );
              })()
            ) : (
              <button onClick={submit} disabled={submitting} className="inline-flex items-center gap-2 bg-[var(--rl-accent)] text-white px-6 py-3 font-semibold hover:bg-[var(--rl-text)] transition-colors disabled:opacity-50" data-testid="builder-pay-btn">
                {submitting ? "Redirecting to Stripe…" : "Pay & launch"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
